const chapter = {
  id: 'principles-of-inheritance',
  slug: 'principles-of-inheritance',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Principles of Inheritance and Variation',
        hindi: 'वंशागति और विविधता के सिद्धान्त',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Genetics', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'mendel',
      title: {
        english: 'Mendelian Genetics',
        hindi: 'मेण्डल की आनुवंशिकी',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '**Gregor Johann Mendel (1856-1863)** — Father of Genetics. Worked on **garden pea (Pisum sativum)** with 7 contrasting characters. Published 1865 (recognised 1900 by de Vries, Correns, Tschermak).',
        hindi: '**ग्रेगर जोहान मेंडल (1856-1863)** - जेनेटिक्स के जनक। **गार्डन पी (पिसम सैटिवम)** पर 7 विपरीत लक्षणों के साथ काम किया। 1865 में प्रकाशित (डे व्रीस, कॉरेंस, त्शेर्मक द्वारा 1900 में मान्यता प्राप्त)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Why pea was ideal: short life cycle, large progeny, distinct contrasting traits, bisexual flowers (self-pollinating; can be cross-pollinated easily).',
        hindi: 'मटर आदर्श क्यों था: लघु जीवन चक्र, बड़ी संतान, विशिष्ट विपरीत लक्षण, उभयलिंगी फूल (स्व-परागण; आसानी से पार-परागण किया जा सकता है)।',
            },
            {
              english: 'Seven characters: stem height (tall/dwarf), flower colour (violet/white), flower position (axial/terminal), pod shape (inflated/constricted), pod colour (green/yellow), seed shape (round/wrinkled), seed colour (yellow/green).',
        hindi: 'सात वर्ण: तने की ऊंचाई (tall/dwarf), फूल का रंग (violet/white), फूल की स्थिति (axial/terminal), फली का आकार (inflated/constricted), फली का रंग (green/yellow), बीज का आकार (round/wrinkled), बीज का रंग (yellow/green)।',
            },
            {
              english: '**Allele** = alternative form of a gene ($T$, $t$).',
        hindi: '**एलील** = जीन का वैकल्पिक रूप ($T$, $t$)।',
            },
            {
              english: '**Homozygous** ($TT/tt$) vs **Heterozygous** ($Tt$). **Genotype** vs **Phenotype**.',
        hindi: '**समयुग्मजी** ($TT/tt$) बनाम **विषमयुग्मजी** ($Tt$)। **जीनोटाइप** बनाम **फेनोटाइप**।',
            },
            {
              english: '**Dominant** trait expressed in F₁; **recessive** masked.',
        hindi: '**प्रमुख** विशेषता F₁ में व्यक्त; **पीछे हटने वाला** नकाबपोश।',
            },
          ],
        },
      ],
    },
    {
      id: 'mendel-laws',
      title: {
        english: 'Mendel\'s Laws',
        hindi: 'मेंडल के नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Law of Dominance** — In a heterozygote, one allele (dominant) expresses itself and masks the other (recessive). Explains 3:1 ratio in F₂.',
        hindi: '**प्रभुत्व का नियम** - हेटेरोज्यगोट में, एक एलील (प्रमुख) खुद को अभिव्यक्त करता है और दूसरे (अप्रभावी) को छुपाता है। F₂ में 3:1 अनुपात समझाता है।',
            },
            {
              english: '**Law of Segregation (Purity of Gametes)** — Two alleles of a gene separate during gamete formation; each gamete carries only one allele. Universal law (no exception).',
        hindi: '**पृथक्करण का नियम (युग्मक की शुद्धता)** - युग्मक निर्माण के दौरान एक जीन के दो एलील अलग हो जाते हैं; प्रत्येक युग्मक में केवल एक एलील होता है। सार्वभौमिक कानून (कोई अपवाद नहीं)।',
            },
            {
              english: '**Law of Independent Assortment** — Alleles of different genes assort independently during gamete formation (applies to genes on different chromosomes). Explains **9:3:3:1** dihybrid F₂ ratio.',
        hindi: '**स्वतंत्र वर्गीकरण का नियम** - युग्मक निर्माण के दौरान विभिन्न जीनों के एलील स्वतंत्र रूप से मिश्रित होते हैं (विभिन्न गुणसूत्रों पर जीन पर लागू होता है)। **9:3:3:1** डाइहाइब्रिड एफ₂ अनुपात बताते हैं।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Cross', 'F₁', 'F₂ Phenotype', 'F₂ Genotype'],
          rows: [
            ['Monohybrid ($TT 	imes tt$)', 'All $Tt$ (tall)', '3 tall : 1 dwarf', '$1,TT : 2,Tt : 1,tt$'],
            ['Dihybrid ($RRYY 	imes rryy$)', 'All $RrYy$', '$9,R	ext{-}Y	ext{-} : 3,R	ext{-}yy : 3,rrY	ext{-} : 1,rryy$', '9 genotypes $(1:2:1)^2$'],
            ['Test cross ($Tt 	imes tt$)', '—', '1 tall : 1 dwarf', '$1,Tt : 1,tt$'],
            ['Back cross (F₁ × homozygous parent)', '—', 'Depends on parent', '—'],
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Test cross is used to determine whether an organism showing dominant phenotype is homozygous ($TT$) or heterozygous ($Tt$).',
        hindi: 'टेस्ट क्रॉस का उपयोग यह निर्धारित करने के लिए किया जाता है कि क्या प्रमुख फेनोटाइप दिखाने वाला जीव समयुग्मजी ($TT$) या विषमयुग्मजी ($Tt$) है।',
          },
        },
      ],
    },
    {
      id: 'deviations',
      title: {
        english: 'Deviations from Mendel\'s Laws',
        hindi: 'मेंडल के नियमों से विचलन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Incomplete dominance** — F₁ is intermediate. Snapdragon (Antirrhinum): Red ($RR$) × White ($rr$) → Pink ($Rr$); F₂ = **1 Red : 2 Pink : 1 White** (1:2:1).',
        hindi: '**अपूर्ण प्रभुत्व** — F₁ मध्यवर्ती है। स्नैपड्रैगन (एंटीरहिनम): लाल ($RR$) × सफेद ($rr$) → गुलाबी ($Rr$); F₂ = **1 लाल : 2 गुलाबी : 1 सफेद** (1:2:1).',
            },
            {
              english: '**Codominance** — both alleles expressed independently. **ABO blood groups** in humans (alleles $I^A$, $I^B$, $i$):',
        hindi: '**कोडोमिनेंस** - दोनों एलील्स स्वतंत्र रूप से व्यक्त हुए। **मनुष्यों में ABO रक्त समूह** (एलील $I^A$, $I^B$, $i$):',
            },
            {
              english: '• $I^A I^A$ or $I^A i 	o A$; $I^B I^B$ or $I^B i 	o B$; **$I^A I^B 	o AB$ (codominant)**; $ii 	o O$.',
        hindi: '• $I^A I^A$ या $I^A i o A$; $I^B I^B$ या $I^B i o B$; **$I^A I^B o AB$ (कोडप्रमुख)**; $ii o O$.',
            },
            {
              english: '**Multiple alleles** — more than two alleles for a gene at population level (ABO).',
        hindi: '**एकाधिक एलील** - जनसंख्या स्तर (एबीओ) पर एक जीन के लिए दो से अधिक एलील।',
            },
            {
              english: '**Pleiotropy** — single gene affects multiple traits. Examples: phenylketonuria, sickle-cell anaemia, starch synthesis (round/wrinkled in pea).',
        hindi: '**प्लियोट्रॉपी** - एकल जीन कई लक्षणों को प्रभावित करता है। उदाहरण: फेनिलकेटोनुरिया, सिकल सेल एनीमिया, स्टार्च संश्लेषण (मटर में round/wrinkled)।',
            },
            {
              english: '**Polygenic inheritance** — trait controlled by many genes; shows continuous variation. Human skin colour (3 genes A, B, C); height. **Phenotype is sum of contributions**.',
        hindi: '**पॉलीजेनिक वंशानुक्रम** - कई जीनों द्वारा नियंत्रित लक्षण; निरंतर भिन्नता दर्शाता है। मानव त्वचा का रंग (3 जीन ए, बी, सी); ऊंचाई। **फेनोटाइप योगदान का योग है**।',
            },
          ],
        },
      ],
    },
    {
      id: 'chromosomal-theory',
      title: {
        english: 'Chromosomal Theory & Linkage',
        hindi: 'गुणसूत्र सिद्धान्त और सहलग्नता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Sutton & Boveri (1902)** — proposed Chromosomal Theory of Inheritance: behaviour of chromosomes parallels behaviour of genes (Mendel\'s factors).',
        hindi: '**सटन और बोवेरी (1902)** - वंशानुक्रम का प्रस्तावित क्रोमोसोमल सिद्धांत: गुणसूत्रों का व्यवहार जीन (मेंडल के कारक) के व्यवहार के समानांतर होता है।',
            },
            {
              english: '**T.H. Morgan** — worked on Drosophila melanogaster; established chromosomal basis & **linkage**.',
        hindi: '**वां। मॉर्गन** - ड्रोसोफिला मेलानोगास्टर पर काम किया; स्थापित गुणसूत्र आधार और **लिंकेज**।',
            },
            {
              english: '**Linkage** — two or more genes on same chromosome inherited together; lower frequency of recombination than expected.',
        hindi: '**लिंकेज** - एक ही गुणसूत्र पर दो या दो से अधिक जीन एक साथ विरासत में मिले; पुनर्संयोजन की अपेक्षा से कम आवृत्ति।',
            },
            {
              english: '**Recombination** — new combinations of alleles via crossing over. **Recombination frequency** ∝ distance between genes (1 % recombination = 1 map unit / centiMorgan).',
        hindi: '**पुनर्संयोजन** - क्रॉसिंग ओवर के माध्यम से एलील्स के नए संयोजन। **पुनर्संयोजन आवृत्ति** ∝ जीन के बीच की दूरी (1 % recombination = 1 map unit / centiMorgan)।',
            },
            {
              english: '**Alfred Sturtevant** — used recombination frequencies to construct **genetic maps**.',
        hindi: '**अल्फ्रेड स्टुरटेवेंट** - **आनुवंशिक मानचित्र** के निर्माण के लिए पुनर्संयोजन आवृत्तियों का उपयोग किया।',
            },
          ],
        },
      ],
    },
    {
      id: 'sex-determination',
      title: {
        english: 'Sex Determination & Sex-Linked Inheritance',
        hindi: 'लिंग निर्धारण और लिंग-सहलग्न वंशागति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**XX – XY** type (humans, Drosophila): female XX (homogametic), male XY (heterogametic).',
        hindi: '**XX - XY** प्रकार (मानव, ड्रोसोफिला): महिला XX (होमोगैमेटिक), पुरुष XY (हेटरोगैमेटिक)।',
            },
            {
              english: '**XX – XO** (grasshopper): male has only X (no Y).',
        hindi: '**XX - XO** (टिड्डा): नर में केवल X होता है (Y नहीं)।',
            },
            {
              english: '**ZW – ZZ** (birds, some fish): female ZW heterogametic; male ZZ.',
        hindi: '**ZW - ZZ** (पक्षी, कुछ मछलियाँ): मादा ZW विषमलैंगिक; पुरुष ZZ.',
            },
            {
              english: '**Haplodiploidy** (honeybees): female $2n$ from fertilised egg; male (drone) $n$ from unfertilised egg (parthenogenesis).',
        hindi: '**हैप्लोडिप्लोइडी** (मधुमक्खियाँ): निषेचित अंडे से मादा $2n$; नर (ड्रोन) अनिषेचित अंडे (पार्थेनोजेनेसिस) से $n$।',
            },
            {
              english: '**Sex-linked disorders** — genes on X chromosome:',
        hindi: '**सेक्स-लिंक्ड विकार** - एक्स गुणसूत्र पर जीन:',
            },
            {
              english: '• **Haemophilia** — recessive X-linked; "Royal disease" (Queen Victoria carrier). $X^H X^H$ normal female; $X^H X^h$ carrier; $X^h X^h$ diseased; $X^H Y$ normal male; $X^h Y$ haemophilic male.',
        hindi: '• **हीमोफीलिया** - रिसेसिव एक्स-लिंक्ड; "शाही रोग" (रानी विक्टोरिया वाहक)। $X^H X^H$ सामान्य महिला; $X^H X^h$ वाहक; $X^h X^h$ रोगग्रस्त; $X^H Y$ सामान्य पुरुष; $X^h Y$ हीमोफिलिक पुरुष।',
            },
            {
              english: '• **Colour blindness** (red-green) — X-linked recessive. ~8 % males, ~0.4 % females affected.',
        hindi: '• **रंग अंधापन** (लाल-हरा) - एक्स-लिंक्ड रिसेसिव। ~8% पुरुष, ~0.4% महिलाएँ प्रभावित।',
            },
            {
              english: '• **Duchenne muscular dystrophy** — X-linked recessive.',
        hindi: '• **ड्युचेन मस्कुलर डिस्ट्रॉफी** - एक्स-लिंक्ड रिसेसिव। **ऑटोसोमल रिसेसिव विकार**: सिकल-सेल एनीमिया (HbS - β-श्रृंखला की स्थिति 6 पर ग्लू→वैल), फेनिलकेटोनुरिया, थैलेसीमिया (α और β)। **ऑटोसोमल डोमिनेंट**: हंटिंगटन कोरिया, मायोटोनिक डिस्ट्रोफी।',
            },
            {
              english: '**Autosomal recessive disorders**: sickle-cell anaemia (HbS — Glu→Val at position 6 of β-chain), phenylketonuria, thalassaemia (α & β).',
        hindi: '**ऑटोसोमल रिसेसिव विकार**: सिकल सेल एनीमिया (एचबीएस - ग्लू→बीटा-श्रृंखला की स्थिति 6 पर वैल), फेनिलकेटोनुरिया, थैलेसीमिया (α और β)।',
            },
            {
              english: '**Autosomal dominant**: Huntington\'s chorea, Myotonic dystrophy.',
        hindi: '**ऑटोसोमल डोमिनेंट**: हंटिंगटन कोरिया, मायोटोनिक डिस्ट्रोफी।',
            },
            {
              english: 'Chromosomal disorders: **Down\'s syndrome** (trisomy 21 — Langdon Down 1866); **Klinefelter\'s** ($47,XXY$ — male, gynecomastia, sterile); **Turner\'s** ($45,X0$ — female, sterile, short stature).',
        hindi: 'गुणसूत्र संबंधी विकार: **डाउन सिंड्रोम** (ट्राइसॉमी 21 - लैंगडन डाउन 1866); **क्लाइनफेल्टर** ($47,XXY$ - पुरुष, गाइनेकोमेस्टिया, बाँझ); **टर्नर** ($45,X0$ - महिला, बाँझ, छोटा कद)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Monohybrid F}_2: 3:1 \\text{ phenotype} \\;|\\; 1:2:1 \\text{ genotype}',
      desc: {
        english: '',
        hindi: 'मोनोहाइब्रिड एफ 2: 3:1 फेनोटाइप \\;|\\; 1:2:1 जीनोटाइप',
      },
    },
    {
      latex: '\\text{Dihybrid F}_2: 9:3:3:1',
      desc: {
        english: '',
        hindi: 'डायहाइब्रिड एफ 2: 9:3:3:1',
      },
    },
    {
      latex: '\\text{Test cross: } Tt \\times tt \\to 1:1',
      desc: {
        english: '',
        hindi: 'टेस्ट क्रॉस: टीटी टीटी 1:1',
      },
    },
    {
      latex: '\\text{Incomplete dominance F}_2: 1:2:1',
      desc: {
        english: '',
        hindi: 'अधूरा प्रभुत्व एफ 2:1:2:1',
      },
    },
    {
      latex: 'I^A I^B \\to AB \\text{ (codominance)}',
      desc: {
        english: '',
        hindi: 'आई ए आई बी एबी (सहप्रभाव)',
      },
    },
    {
      latex: '\\text{1 cM} = 1\\% \\text{ recombination frequency}',
      desc: {
        english: '',
        hindi: '1 सीएम = 1\\% पुनर्संयोजन आवृत्ति',
      },
    },
    {
      latex: '\\text{Down\'s = trisomy 21; Turner\'s = 45,X0; Klinefelter\'s = 47,XXY}',
      desc: {
        english: '',
        hindi: 'डाउन्स = ट्राइसॉमी 21; टर्नर का = 45,X0; क्लाइनफेल्टर = 47,XXY',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Mendel',
      problem: {
        english: 'Phenotypic ratio of dihybrid test cross is:',
        hindi: 'डायहाइब्रिड परीक्षण क्रॉस का फेनोटाइपिक अनुपात है:',
      },
      answer: {
        english: '1 : 1 : 1 : 1',
        hindi: '1 : 1 : 1 : 1',
      },
      solution: {
        english: 'F₁ $RrYy 	imes rryy$ → 4 equal classes.',
        hindi: 'F₁ $RrYy imes rryy$ → 4 समान वर्ग।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Blood group',
      problem: {
        english: 'Marriage of A blood group ($I^A i$) × B blood group ($I^B i$) — possible offspring blood groups:',
        hindi: 'A रक्त समूह ($I^A i$) × B रक्त समूह ($I^B i$) का विवाह - संभावित संतान रक्त समूह:',
      },
      answer: {
        english: 'A, B, AB, O (all four)',
        hindi: 'ए, बी, एबी, ओ (सभी चार)',
      },
      solution: {
        english: 'Genotype combinations: $I^A I^B$ (AB), $I^A i$ (A), $I^B i$ (B), $ii$ (O).',
        hindi: 'जीनोटाइप संयोजन: $I^A I^B$ (AB), $I^A i$ (A), $I^B i$ (B), $ii$ (O)।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Sex linkage',
      problem: {
        english: 'Haemophilia is inherited as:',
        hindi: 'हीमोफीलिया इस प्रकार विरासत में मिला है:',
      },
      answer: {
        english: 'X-linked recessive',
        hindi: 'एक्स-लिंक्ड रिसेसिव',
      },
      solution: {
        english: 'Males with $X^h Y$ are affected; females usually carriers.',
        hindi: '$X^h Y$ वाले पुरुष प्रभावित होते हैं; मादाएं आमतौर पर वाहक होती हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Chromosomal',
      problem: {
        english: 'Karyotype $47,XXY$ corresponds to:',
        hindi: 'कैरियोटाइप $47,XXY$ इससे मेल खाता है:',
      },
      answer: {
        english: 'Klinefelter\'s syndrome',
        hindi: 'क्लाइनफेल्टर सिंड्रोम',
      },
      solution: {
        english: 'Extra X in male; gynecomastia, sterility.',
        hindi: 'पुरुष में अतिरिक्त एक्स; गाइनेकोमेस्टिया, बाँझपन।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Pleiotropy',
      problem: {
        english: 'A single gene affecting multiple traits is termed:',
        hindi: 'कई लक्षणों को प्रभावित करने वाले एक जीन को कहा जाता है:',
      },
      answer: {
        english: 'Pleiotropy',
        hindi: 'प्लियोट्रॉपी',
      },
      solution: {
        english: 'Example: sickle-cell anaemia.',
        hindi: 'उदाहरण: सिकल सेल एनीमिया।',
      },
    },
  ],
  next: {
    slug: 'molecular-basis-inheritance',
    title: 'Molecular Basis of Inheritance',
  },
};

export default chapter;
